import { useState } from 'react'
import { Palette, ImageIcon, Blend, X, Plus } from 'lucide-react'
import { MediaLibrary } from '../media/MediaLibrary'
import Toggle from '../../shared/ui/Toggle'

const DEFAULT_GRADIENT_STOPS = [
  { color: '#E85D26', position: 0 },
  { color: '#1a3a5c', position: 100 },
]

/**
 * Parse une valeur de fond en état interne.
 */
const parseBackground = (value) => {
  const defaults = {
    type: 'color',
    color: '#1a3a5c',
    gradientAngle: 135,
    gradientStops: DEFAULT_GRADIENT_STOPS.map(s => ({ ...s })),
    image: '',
    overlayEnabled: false,
    overlayColor: '#000000',
    overlayOpacity: 40,
  }

  if (!value) return defaults

  // Legacy : string hex ou URL
  if (typeof value === 'string') {
    if (value.startsWith('#') || value.startsWith('rgb')) {
      return { ...defaults, type: 'color', color: value }
    }
    if (value.startsWith('http') || value.startsWith('/') || value.startsWith('data:')) {
      return { ...defaults, type: 'image', image: value }
    }
    return defaults
  }

  if (typeof value === 'object') {
    const hasOverlay = !!(value.overlay?.color && value.overlay?.opacity > 0)
    return {
      ...defaults,
      type: value.type || 'color',
      color: value.color || '#1a3a5c',
      gradientAngle: value.gradient?.angle ?? 135,
      gradientStops: value.gradient?.stops?.length >= 2
        ? value.gradient.stops.map(s => ({ ...s }))
        : DEFAULT_GRADIENT_STOPS.map(s => ({ ...s })),
      image: value.image || '',
      overlayEnabled: hasOverlay,
      overlayColor: value.overlay?.color || '#000000',
      overlayOpacity: Math.round((value.overlay?.opacity ?? 0.4) * 100),
    }
  }

  return defaults
}

/**
 * Sérialise l'état interne en valeur de fond (format objet).
 */
const serializeBackground = (state) => {
  if (state.type === 'color') {
    return { type: 'color', color: state.color }
  }
  if (state.type === 'gradient') {
    return {
      type: 'gradient',
      gradient: {
        angle: state.gradientAngle,
        stops: state.gradientStops,
      },
    }
  }
  // image
  const result = { type: 'image', image: state.image }
  if (state.overlayEnabled && state.overlayColor) {
    result.overlay = { color: state.overlayColor, opacity: state.overlayOpacity / 100 }
  }
  return result
}

const isValidHex = (hex) => /^#[0-9a-fA-F]{6}$/.test(hex)

const gradientCss = (angle, stops) => {
  const sorted = [...stops].sort((a, b) => a.position - b.position)
  return `linear-gradient(${angle}deg, ${sorted.map(s => `${s.color} ${s.position}%`).join(', ')})`
}

/**
 * Éditeur d'arrière-plan : couleur unie, dégradé linéaire, ou image avec overlay.
 */
export const BackgroundEditor = ({ value, onSave, onClose }) => {
  const [state, setState] = useState(() => parseBackground(value))
  const [hexInput, setHexInput] = useState(() => parseBackground(value).color)
  const [overlayHexInput, setOverlayHexInput] = useState(() => parseBackground(value).overlayColor)

  const update = (patch) => setState(prev => ({ ...prev, ...patch }))

  // --- Couleur unie ---
  const handleColorPickerChange = (e) => {
    const hex = e.target.value
    update({ color: hex })
    setHexInput(hex)
  }
  const handleHexInput = (e) => {
    const raw = e.target.value
    setHexInput(raw)
    const n = raw.startsWith('#') ? raw : `#${raw}`
    if (isValidHex(n)) update({ color: n })
  }
  const handleHexBlur = () => {
    const n = hexInput.startsWith('#') ? hexInput : `#${hexInput}`
    if (!isValidHex(n)) setHexInput(state.color)
    else setHexInput(n)
  }

  // --- Overlay ---
  const handleOverlayPickerChange = (e) => {
    const hex = e.target.value
    update({ overlayColor: hex })
    setOverlayHexInput(hex)
  }
  const handleOverlayHexInput = (e) => {
    const raw = e.target.value
    setOverlayHexInput(raw)
    const n = raw.startsWith('#') ? raw : `#${raw}`
    if (isValidHex(n)) update({ overlayColor: n })
  }
  const handleOverlayHexBlur = () => {
    const n = overlayHexInput.startsWith('#') ? overlayHexInput : `#${overlayHexInput}`
    if (!isValidHex(n)) setOverlayHexInput(state.overlayColor)
    else setOverlayHexInput(n)
  }

  // --- Gradient stops ---
  const updateStop = (idx, patch) => {
    const stops = state.gradientStops.map((s, i) => i === idx ? { ...s, ...patch } : s)
    update({ gradientStops: stops })
  }
  const addStop = () => {
    const stops = [...state.gradientStops, { color: '#ffffff', position: 50 }]
    update({ gradientStops: stops })
  }
  const deleteStop = (idx) => {
    update({ gradientStops: state.gradientStops.filter((_, i) => i !== idx) })
  }

  const handleSave = () => onSave(serializeBackground(state))

  return (
    <div className="flex flex-col gap-4">

      {/* Onglets */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
        <TypeTab active={state.type === 'color'} onClick={() => update({ type: 'color' })} icon={<Palette size={13} />} label="Color" />
        <TypeTab active={state.type === 'gradient'} onClick={() => update({ type: 'gradient' })} icon={<Blend size={13} />} label="Gradient" />
        <TypeTab active={state.type === 'image'} onClick={() => update({ type: 'image' })} icon={<ImageIcon size={13} />} label="Image" />
      </div>

      {/* === Couleur unie === */}
      {state.type === 'color' && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-2">Background color</label>
          <div className="flex items-center gap-2">
            <ColorSwatch color={state.color} onChange={handleColorPickerChange} size="lg" />
            <input
              type="text"
              value={hexInput}
              onChange={handleHexInput}
              onBlur={handleHexBlur}
              placeholder="#rrggbb"
              maxLength={7}
              className="flex-1 px-3 py-2 text-sm font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent"
            />
            <div className="w-16 h-9 rounded-lg border border-gray-200 flex-shrink-0" style={{ backgroundColor: state.color }} />
          </div>
        </div>
      )}

      {/* === Dégradé === */}
      {state.type === 'gradient' && (
        <div className="flex flex-col gap-4">

          {/* Direction */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-medium text-gray-600">Direction</label>
              <div className="flex items-center gap-1.5">
                <input
                  type="number"
                  min="0"
                  max="360"
                  value={state.gradientAngle}
                  onChange={e => update({ gradientAngle: parseInt(e.target.value) || 0 })}
                  className="w-16 px-2 py-1 text-sm font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 text-right"
                />
                <span className="text-xs text-gray-400">°</span>
              </div>
            </div>
            <input
              type="range"
              min="0"
              max="360"
              value={state.gradientAngle}
              onChange={e => update({ gradientAngle: parseInt(e.target.value) })}
              className="w-full h-1.5 accent-teal-600"
            />
            {/* Raccourcis angles courants */}
            <div className="flex gap-1 mt-2">
              {[0, 45, 90, 135, 180, 225, 270, 315].map(a => (
                <button
                  key={a}
                  type="button"
                  onClick={() => update({ gradientAngle: a })}
                  className={`flex-1 py-1 text-xs rounded transition-colors ${
                    state.gradientAngle === a
                      ? 'bg-teal-700 text-white'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                >
                  {a}°
                </button>
              ))}
            </div>
          </div>

          {/* Stops */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Gradient colors</label>
            <div className="flex flex-col gap-2">
              {state.gradientStops.map((stop, idx) => (
                <StopRow
                  key={idx}
                  stop={stop}
                  onChange={patch => updateStop(idx, patch)}
                  onDelete={() => deleteStop(idx)}
                  canDelete={state.gradientStops.length > 2}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={addStop}
              className="mt-2 flex items-center gap-1.5 text-xs text-teal-700 hover:text-teal-900 transition-colors"
            >
              <Plus size={13} />
              Add a color
            </button>
          </div>

          {/* Aperçu */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Preview</label>
            <div
              className="h-12 rounded-lg border border-gray-200"
              style={{ backgroundImage: gradientCss(state.gradientAngle, state.gradientStops) }}
            />
          </div>
        </div>
      )}

      {/* === Image === */}
      {state.type === 'image' && (
        <div className="flex flex-col gap-4">
          <InlineMediaPicker
            value={state.image}
            onChange={url => update({ image: url })}
          />

          {/* Overlay */}
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-xs font-medium text-gray-700">Color overlay</span>
              <Toggle
                checked={state.overlayEnabled}
                onChange={() => update({ overlayEnabled: !state.overlayEnabled })}
                label="Color overlay"
              />
            </div>

            {state.overlayEnabled && (
              <div className="border-t border-gray-100 px-4 py-3 flex flex-col gap-3 bg-gray-50/50">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Color</label>
                  <div className="flex items-center gap-2">
                    <ColorSwatch color={state.overlayColor} onChange={handleOverlayPickerChange} />
                    <input
                      type="text"
                      value={overlayHexInput}
                      onChange={handleOverlayHexInput}
                      onBlur={handleOverlayHexBlur}
                      placeholder="#000000"
                      maxLength={7}
                      className="flex-1 px-3 py-1.5 text-sm font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-medium text-gray-500">Intensity</label>
                    <span className="text-xs font-medium text-gray-700 tabular-nums">{state.overlayOpacity}%</span>
                  </div>
                  <input type="range" min="0" max="100" step="5" value={state.overlayOpacity} onChange={e => update({ overlayOpacity: parseInt(e.target.value) })} className="w-full h-1.5 accent-teal-600" />
                  <div className="flex justify-between text-xs text-gray-400 mt-0.5">
                    <span>Transparent</span>
                    <span>Opaque</span>
                  </div>
                </div>

                {state.image && (
                  <OverlayPreview image={state.image} color={state.overlayColor} opacity={state.overlayOpacity / 100} />
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="builder-editor-actions">
        <button type="button" onClick={onClose} className="btn btn-secondary">
          Annuler
        </button>
        <button type="button" onClick={handleSave} className="btn btn-primary">
          Appliquer
        </button>
      </div>
    </div>
  )
}

/* ── Sous-composants ─────────────────────────────────────────── */

const TypeTab = ({ active, onClick, icon, label }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-all ${
      active ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500 hover:text-gray-700'
    }`}
  >
    {icon}
    {label}
  </button>
)

const ColorSwatch = ({ color, onChange, size = 'md' }) => {
  const sz = size === 'lg' ? 'w-9 h-9' : 'w-8 h-8'
  return (
    <div className="relative flex-shrink-0">
      <div className={`${sz} rounded-lg border border-gray-200 overflow-hidden shadow-sm cursor-pointer`} style={{ backgroundColor: color }}>
        <input type="color" value={color} onChange={onChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
      </div>
    </div>
  )
}

const StopRow = ({ stop, onChange, onDelete, canDelete }) => (
  <div className="flex items-center gap-2">
    <ColorSwatch color={stop.color} onChange={e => onChange({ color: e.target.value })} />
    <input
      type="text"
      value={stop.color}
      onChange={e => {
        const v = e.target.value.startsWith('#') ? e.target.value : `#${e.target.value}`
        if (isValidHex(v)) onChange({ color: v })
      }}
      maxLength={7}
      className="w-24 px-2 py-1.5 text-xs font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600"
    />
    <input
      type="range"
      min="0"
      max="100"
      value={stop.position}
      onChange={e => onChange({ position: parseInt(e.target.value) })}
      className="flex-1 h-1.5 accent-teal-600"
    />
    <span className="text-xs text-gray-500 tabular-nums w-8 text-right">{stop.position}%</span>
    {canDelete && (
      <button type="button" onClick={onDelete} className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0">
        <X size={13} />
      </button>
    )}
  </div>
)

/**
 * Sélecteur d’image du BackgroundEditor.
 *
 * Il n’a plus de grille à lui : il ouvre LA médiathèque, la même pop-in plein
 * écran que les champs image — un seul stock, une seule surface, une seule
 * façon de chercher. Le champ URL reste pour une image hébergée ailleurs.
 */
const InlineMediaPicker = ({ value, onChange }) => {
  const [libraryOpen, setLibraryOpen] = useState(false);

  return (
    <div className="flex flex-col gap-3">

      {/* Aperçu + effacer */}
      {value && (
        <div className="relative h-20 rounded-lg overflow-hidden border border-gray-200 bg-gray-100">
          <img src={value} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display = "none" }} />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute top-1 right-1 p-0.5 bg-white/80 rounded-full text-gray-600 hover:text-red-600 transition-colors"
            aria-label="Retirer l’image de fond"
          >
            <X size={13} aria-hidden="true" />
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={() => setLibraryOpen(true)}
        className="flex items-center justify-center gap-2 py-2.5 px-3 border-2 border-dashed border-gray-200 bg-gray-50 rounded-lg text-xs text-gray-500 hover:border-gray-300 hover:bg-gray-100/60 transition-all"
      >
        <ImageIcon size={14} aria-hidden="true" />
        {value ? "Changer d’image" : "Choisir dans la médiathèque"}
      </button>

      {/* URL externe */}
      <div>
        <label htmlFor="background-image-url" className="block text-xs text-gray-400 mb-1">URL externe</label>
        <input
          id="background-image-url"
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="https://exemple.com/image.jpg"
          className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent bg-gray-50"
        />
      </div>

      {libraryOpen && (
        <MediaLibrary
          value={value}
          title="Médiathèque — image de fond"
          onClose={() => setLibraryOpen(false)}
          onPick={item => onChange(item.url)}
        />
      )}
    </div>
  );
};

const OverlayPreview = ({ image, color, opacity }) => {
  const hex = color.replace('#', '')
  const r = parseInt(hex.slice(0, 2), 16) || 0
  const g = parseInt(hex.slice(2, 4), 16) || 0
  const b = parseInt(hex.slice(4, 6), 16) || 0
  return (
    <div className="relative h-16 rounded-lg overflow-hidden border border-gray-200">
          <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0" style={{ backgroundColor: `rgba(${r},${g},${b},${opacity})` }} />
      <span className="absolute bottom-1 right-2 text-white text-xs font-medium drop-shadow">Preview</span>
    </div>
  )
}
